'use client'

import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'

import {
  AppBackground,
  BottomActionContainer,
  Button,
  Drop,
  Input,
  ResponsiveLogo,
  SelectableItemProps,
} from '@ui/components'

import {
  InputContainer,
  InputWrapper,
  Headline5,
  ErrorText,
  DisabledButton, ButtonContent, DisabledButtonText
} from '@/aena/styles' // Importando los estilos del PetAdd

import { ReactComponent as PawIcon } from '@/assets/icons/paw_color.svg'
import { ReactComponent as PawIconFill } from '@/assets/icons/paw.svg'
import { HeaderWithBackAena } from '@/components/headerwithback'

import { useProfile } from '@front/logic'
import { ResponseGetCountries, checkUserStatus } from '@front/apis'
import { changeAppState, setSelectedPet, loadOwnerName, setOwnerCardNumber, setOwnerEmail, setPetLoadingStatus, useAppDispatch } from '@front/store'
import { setPet } from '../my-store'



export const OwnerEdit: FC<{onSubmit?: () => void, touched?: boolean}> = ({onSubmit=() => {}, touched=false}) => {
  const ROAD_TYPE = [
    { id: "CL", title: t('address.roadType.cl'), data: "CL"},
    { id: "CAM", title: t('address.roadType.cam'), data: "CAM" },
    { id: "PLZ", title: t('address.roadType.plz'), data: "PLZ" },
    { id: "AV", title: t('address.roadType.av'), data: "AV" },
    { id: "BLV", title: t('address.roadType.blv'), data: "BLV" },
    { id: "ROAD", title: t('address.roadType.road'), data: "ROAD" },
  ]  
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // 👉 Formik + Yup + APIs desde tu hook
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    getProfileContactInfo,
    getCountries,
  } = useProfile()

  // Carga de perfil
  useEffect(() => {
    getProfileContactInfo()
  }, [])

  // Países con búsqueda (debounce)
  const [countries, setCountries] = useState<ResponseGetCountries[]>([])
  const [countryQuery, setCountryQuery] = useState('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const fetchCountries = async (q = '') => {
    const res = await getCountries({ search: q })
    setCountries(res)
  }

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => fetchCountries(countryQuery), 600)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [countryQuery])

  useEffect(() => {
    fetchCountries('')
  }, [])

  const onCountryPress = (item: SelectableItemProps, _index?: number) => {
    setFieldValue('country', item.id)
  }

  const onRoadTypePress = (item: SelectableItemProps, _index?: number) => {
    setFieldValue('roadType', item.id)
  }

  const dropCountries = useMemo(() => {
    return countries.map((c) => ({
      title: c.country,
      id: c.id,
      data: {},
      onPress: onCountryPress,
      isSelected: values.country === c.id,
    }))
  }, [countries, values.country])

  const validateAndSetUser = async () => {
    const resUserStatus = await checkUserStatus({
      lat: 0,
      lon: 0,
    })

    dispatch(setPetLoadingStatus(true))


    dispatch(
      changeAppState({
        appNavigationState: 'LOGGED_IN_SERVICES',
        isSignUp: true,
        avatar: resUserStatus.data.avatar,
        counters: resUserStatus.data.counters,
        guid: resUserStatus.data.guid,
        petpassRole: resUserStatus.data.petPassRole,
        role: resUserStatus.data.role,
        status: resUserStatus.data.status,
        userName: resUserStatus.data.userName,
        favVetGUID: resUserStatus.data.petpassUserFavVetGUID,
        email: resUserStatus.data.email,
        name: resUserStatus.data.name,
        lastName: resUserStatus.data.lastName,
        phone: resUserStatus.data.phone,
      }),
    )
  }

  const handleSave = async () => {
    onSubmit()
    await handleSubmit()
    await getProfileContactInfo()
    await validateAndSetUser()
    navigate(-1)
  }

  // Habilitado simple: si no hay errores requeridos
  const isFormValid =
    !errors?.name && !errors?.lastName && !errors?.documentId && !errors?.phone && !errors?.email && !errors?.address && !errors?.country && !errors?.zipCode && !errors?.locality && !errors?.province && !errors?.addressInfo && !errors?.addressNumber && !errors?.roadType

  return (
        <BottomActionContainer
          bottomAction={
            isFormValid || touched ? (
              <Button
              title={t('common.update', 'Actualizar')}
              icon={<PawIconFill width={24} height={24} />}
              buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              textColor="background0"
              allowIconAndText
              color="primary"
              onPress={handleSave}
            />              

            ) : (
              <DisabledButton disabled>
                <ButtonContent>
                  <PawIcon width={24} height={24} fill="#1a2732" />
                  <DisabledButtonText>{t('common.update', 'Actualizar')}</DisabledButtonText>
                </ButtonContent>
              </DisabledButton>
            )
          }
        >
          <>

            {/* Nombre */}
            <InputWrapper>
              <Headline5>{t('editProfile.fullname', 'Nombre y Apellidos')} *</Headline5>
            <View style={{ flexDirection: 'row', gap: 16 }}>
            <View style={{ flex: 1 }}>
              <InputContainer>
                <Input
                  inputProps={{
                    value: values.name,
                    onChangeText: handleChange('name'),
                    onBlur: handleBlur('name'),
                    placeholder: t('editProfile.names_placeholder', 'Nombre'),
                  }}
                />
                {errors.name && <ErrorText>{t('editProfile.required_name')}</ErrorText>}
              </InputContainer>
            </View>
              <View style={{ flex: 1 }}>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.lastName,
                      onChangeText: handleChange('lastName'),
                      onBlur: handleBlur('lastName'),
                      placeholder: t('editProfile.lastnames_placeholder', 'Apellidos'),
                    }}
                  />
                  {errors.lastName && <ErrorText>{t('editProfile.required_lastname')}</ErrorText>}
                </InputContainer>
              </View>

            </View>
            </InputWrapper>

            {/* Teléfono / Email */}
            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <Headline5>{t('editProfile.phone', 'Teléfono')} *</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.phone,
                      onChangeText: handleChange('phone'),
                      onBlur: handleBlur('phone'),
                      placeholder: t('editProfile.phone_placeholder', 'Teléfono'),
                      keyboardType: 'phone-pad',
                    }}
                  />
                  {errors.phone && <ErrorText>{t('editProfile.required_phone')}</ErrorText>}
                </InputContainer>
              </View>
              <View style={{ flex: 1 }}>
                <Headline5>Email</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.email,
                      onChangeText: handleChange('email'),
                      onBlur: handleBlur('email'),
                      placeholder: t('editProfile.email_placeholder', 'email@dominio.com'),
                      autoCapitalize: 'none',
                    }}
                  />
                  {errors.email && <ErrorText>{t('editProfile.required_email')}</ErrorText>}
                </InputContainer>
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <Headline5>{t('missingData.dni', 'DNI o NIE')} *</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.documentId,
                      onChangeText: (text: string) => handleChange('documentId')(text.toUpperCase()),
                      onBlur: handleBlur('documentId'),
                      placeholder: t('missingData.dni', 'DNI o NIE'),
                    }}
                  />
                  {errors.documentId && <ErrorText>{t('missingData.dni_required')}</ErrorText>}
                </InputContainer>
              </View>

            </View>

            {/* Dirección / Nº / Info */}
            <View style={{ flexDirection: 'row', gap: 16 }}>
            <View style={{ flex: 0.8 }}>
            <Headline5>{t('editProfile.roadType')}</Headline5>
    <InputContainer>
      <Drop
        variant="outlined"
        color="aena"
        icon={<PawIcon />}
        placeholder={t('editProfile.roadType_placeholder', 'Tipo de via')}
        active={ROAD_TYPE.findIndex((obj) => obj.id === values.roadType)}
        selectedItem={ROAD_TYPE.findIndex((obj) => obj.id === values.roadType)}
        data={ROAD_TYPE}
        onPress={onRoadTypePress}
        allowIconAndText
        buttonText={t('common.continue')}
      />
      {errors.roadType && <ErrorText>{t('editProfile.roadType_placeholder')}</ErrorText>}
    </InputContainer>
  </View>    
              <View style={{ flex: 1.4 }}>
                <Headline5>{t('editProfile.address', 'Dirección')} *</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.address,
                      onChangeText: handleChange('address'),
                      onBlur: handleBlur('address'),
                      placeholder: t('editProfile.address_placeholder', 'Calle / Avenida'),
                    }}
                  />
                  {errors.address && <ErrorText>{t('editProfile.required_address')}</ErrorText>}
                </InputContainer>
              </View>
              <View style={{ flex: 0.4 }}>
                <Headline5>{t('editProfile.addressNumber', 'Nº')} *</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.addressNumber,
                      onChangeText: handleChange('addressNumber'),
                      onBlur: handleBlur('addressNumber'),
                      placeholder: t('editProfile.addressNumber_placeholder', 'Nº'),
                    }}
                  />
                  {errors.addressNumber && <ErrorText>{t('editProfile.addressNumber_placeholder')}</ErrorText>}
                </InputContainer>
              </View>
            </View>

            <InputWrapper>
              <Headline5>{t('editProfile.addressInfo', 'Info adicional')}</Headline5>
              <InputContainer>
                <Input
                  inputProps={{
                    value: values.addressInfo,
                    onChangeText: handleChange('addressInfo'),
                    onBlur: handleBlur('addressInfo'),
                    placeholder: t('editProfile.addressInfo_placeholder', 'Escalera, piso, puerta...'),
                  }}
                />
                {errors.addressInfo && <ErrorText>{t('editProfile.addressInfo_placeholder')}</ErrorText>}
              </InputContainer>
            </InputWrapper>

            {/* País (Drop con búsqueda), CP, Localidad, Provincia */}
            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <Headline5>{t('addPetAdress.country', 'País')} *</Headline5>
                <InputContainer>
                  <Drop
                    placeholder={t('addPetAdress.country', 'País')}
                    icon={<PawIcon width={16} height={16} fill="white" />}
                    active={dropCountries.findIndex((obj) => obj.id === values.country)}
                    selectedItem={dropCountries.findIndex((obj) => obj.id === values.country)}
                    data={dropCountries}
                    onPress={onCountryPress}
                    color="aena"
                    variant="outlined"
                    allowIconAndText
                    inputProps={{
                      inputProps: {
                        value: countryQuery,
                        onChangeText: (val: string) => setCountryQuery(val),
                        placeholder: t('addPetAdress.placeholderSearch', 'Buscar país'),
                      },
                    }}
                  />
                  {errors.country && <ErrorText>{t('missingData.country_required')}</ErrorText>}
                </InputContainer>
              </View>
              <View style={{ flex: 1 }}>
                <Headline5>{t('editProfile.zipCode', 'Código postal')} *</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.zipCode,
                      onChangeText: handleChange('zipCode'),
                      onBlur: handleBlur('zipCode'),
                      placeholder: t('editProfile.zipCode_placeholder', 'CP'),
                    }}
                  />
                  {errors.zipCode && <ErrorText>{t('missingData.zipCode_required')}</ErrorText>}
                </InputContainer>
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <Headline5>{t('editProfile.locality', 'Localidad')} *</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.locality,
                      onChangeText: handleChange('locality'),
                      onBlur: handleBlur('locality'),
                      placeholder: t('editProfile.locality_placeholder', 'Ciudad / Población'),
                    }}
                  />
                  {errors.locality && <ErrorText>{t('missingData.locality_required')}</ErrorText>}
                </InputContainer>
              </View>
              <View style={{ flex: 1 }}>
                <Headline5>{t('editProfile.province', 'Provincia')} *</Headline5>
                <InputContainer>
                  <Input
                    inputProps={{
                      value: values.province,
                      onChangeText: handleChange('province'),
                      onBlur: handleBlur('province'),
                      placeholder: t('editProfile.province_placeholder', 'Provincia'),
                    }}
                  />
                  {errors.province && <ErrorText>{t('missingData.province_required')}</ErrorText>}
                </InputContainer>
              </View>
            </View>

          </>
        </BottomActionContainer>
  )
}

export const OwnerEditPage: FC = () => 
   <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>
        <HeaderWithBackAena title={t('common.ownerinfo', 'Información del dueño')} />
        <OwnerEdit />
    </View>
  </AppBackground>


export default OwnerEditPage
